import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "components";
import { formatUnixTimestamp } from "pages/Opportunities/utils/featureRule";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Toaster } from "sonner";
import ConfirmBookingModal from "./ConfirmBookingModal";
import Overview from "./tabs/Overview";
import AmountCharges from "./tabs/AmountCharges";
import { generic_order_status_options } from "common/filters";
import Customers from "./tabs/Customers";
import { Delivery } from "./tabs/Delivery";
import Payments from "./tabs/Payments";
import { useOrder } from "./hooks/useOrders";
import { Milestone, Customers as TypeCustomers } from "./types/order";
import OtherDetails from "./tabs/OtherDetails";
import { getButtonState, getLiveStage } from "./utils/stageconfig";
import { Button } from "@/components/ui/button";
import MilestoneTracker from "./components/milestonetracker";


const GenericOrderDetailsPage: React.FC = () => {
  const [isConfirmBooking, setIsConfirmBooking] = useState<boolean>(false);
  const { id } = useParams();
  const param = useParams();
  const [activeTab, setTab] = useState<string>("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [note, setNote] = useState<string>("");
  const [tempNote, setTempNote] = useState<string>(note);  
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const {
    orderDetails,
    isLoading,
  } = useOrder(id);
  const milestone = orderDetails?.milestones || [];
  const { disabled } = getButtonState('confirmOrder', milestone as Milestone[]);
  const deliveryButtonState = getButtonState('confirmDelivery', milestone as Milestone[]);
  const paymentButtonState = getButtonState('submitPayment', milestone);
  const documentsButtonState = getButtonState('uploadDocuments', milestone);
  const isDisabledAll = getButtonState('disableAll', milestone);
  const liveStage = getLiveStage(milestone as Milestone[]) 
  

  // Use our custom hook

  const openDetailNote = () => {
    setIsEditing(true);
  };

  // for TCS
  // const handleTCSSave = async (order_id: number, note: string, tcs_amount: number) => {
  //   const result = await updateTCS(order_id, note, tcs_amount);
  //   if (result) {
  //     setIsTCSEditing(false);
  //     setTcsValue("0.00");
  //     setIsChecked(false);
  //   }
  // };

  const handleConfirmSubmit = () => {
    // Submit logic here
    setIsConfirmBooking(false);
  };

  // const handleInvoiceDocument = () => {
  //   if (orderDetails?.invoice_document?.url) {
  //     downloadFile(orderDetails.invoice_document.url, "invoice_document");
  //     return;
  //   }
  //   setShowUploadDocumentType("Invoice");
  // };

  const onTabChange = (value: string) => {
    setTab(value);
  };

  const colors = generic_order_status_options.find(
    (status) => status.label.toLocaleLowerCase() === liveStage?.stage?.toLowerCase()
  );

  let tabsdata = [
    {
      id: 1,
      label: "Overview",
      value: "overview",
      enabled: true,
      visible: true,
      content: "Make changes to your account here.",
    },
    {
      id: 2,
      label: "Amount & Charges",
      value: "amount-charges",
      enabled: true,
      visible: true,
      content: "Change your password here.",
    },
    {
      id: 3,
      label: "Customers",
      value: "customers",
      enabled: true,
      visible: true,
      content: "Change your password here.",
    },
    {
      id: 7,
      label: "Travellers",
      value: "t",
      enabled: true,
      visible: false,
      content: "Change your password here.",
    },
    {
      id: 4,
      label: "Delivery",
      value: "delivery",
      enabled: true,
      visible: true,
      content: "Change your password here.",
    },
    {
      id: 5,
      label: "Payments",
      value: "payments",
      visible: true,
      enabled: true,
      content: "Change your password here.",
    },
    {
      id: 6,
      label: "Other Details",
      value: "other-details",
      visible: true,
      enabled: true,
      content: "Change your password here.",
    },
  ];


  const transformNewToOldSchema = (newData: TypeCustomers) => {
    // Create a map to group products by customer
    const customersMap = new Map<number, any>();
    
    newData?.product_customer?.forEach((product) => {
      const customerId = product.created_for;
      
      // If customer doesn't exist in map, create entry
      if (!customersMap.has(customerId)) {
        customersMap.set(customerId, {
          id: customerId,
          name: product.customer_detail?.full_name || `Customer ${customerId}`,
          product_detail: []
        });
      }
      
      // Add product to customer's product_detail array
      const customer = customersMap.get(customerId);
      customer.product_detail.push({
        id: product.id,
        name: product.name,
        currency: product.country_currency_want,
        quantity: product.quantity,
        status: product.status,
        reason_for_reject: product.reason_for_reject || "",
        // Include any other fields your component expects
        subCategory: product.subCategory
      });
    });
    
    // Convert map values to array
    return Array.from(customersMap.values());
  };

  const oldSchemaData = transformNewToOldSchema(orderDetails?.customers as TypeCustomers);
  const customerCount = new Set<number>(orderDetails?.customers?.product_customer?.filter(product=>(product.status === 'Accept' || !product?.status)).map(product => product.created_for));

  return (
    <>
      {isLoading && <Spinner />}

      <div className="flex flex-col gap-5 h-full w-ful overflow-y-scroll overflow-x-hidden p-3 bg-white-A700 border rounded-xl">
        <div className="flex w-full gap-8 border-b-2 justify-between pb-5">
          <div className="flex justify-between items-center w-full">
            <div className=" flex flex-col gap-2">
              <div className="flex flex-row items-center gap-2">
                <h3 className="font-semibold leading-4 text-lg text-black-label">
                  OD-{orderDetails?.metadata.order_id}
                </h3>
                <span className="text-gray-400">•</span>
                <h3 className="font-semibold leading-4 text-lg text-black-label">
                  {orderDetails?.metadata?.created_by}
                </h3>
                <span className="text-gray-400">•</span>
                <span className="font-semibold leading-4 text-lg text-black-label">{orderDetails?.metadata?.product_group}</span>               
              </div>

              <div>
                <p className="font-normal leading-[22.4px] text-sm text-[#7E7E7E]">
                  created on {formatUnixTimestamp(orderDetails?.metadata?.created_at as number)} by {orderDetails?.metadata?.created_by || "N/A"} | {orderDetails?.metadata?.source || "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {
                !disabled &&
                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsConfirmBooking(true)}
                    disabled={disabled}
                    variant="ghost"
                    className="rounded-lg disabled:opacity-40 py-[11px] px-4 flex gap-2 border border-[#165FE3] text-[#165FE3] font-semibold text-sm bg-white"
                  >
                    Accept
                  </Button>
                  <Button
                    onClick={() => setIsConfirmBooking(true)}
                    disabled={disabled}
                    variant="ghost"
                    className="rounded-lg disabled:opacity-40 py-[11px] px-4 flex gap-2 border border-[#EB5757] text-[#EB5757] font-semibold text-sm bg-white"
                  >
                    Reject
                  </Button>              
                </div>
              }
              <span style={{
                color: colors?.textColor,
                backgroundColor: colors?.bgColor,
              }} className="rounded-full capitalize py-0.5 px-2  font-semibold text-sm ">
                {liveStage?.sub_stage}
              </span>
            </div>
          </div>
        </div>

        {/* all tabs */}
        <Tabs
          value={activeTab}
          onValueChange={onTabChange}
          className=" sm:max-w-7xl sm:overflow-hidden"
        >
          <TabsList className="border-b border-[#C4D7F9] w-full hide-scrollbar flex justify-between rounded-none gap-2 h-12">
            {tabsdata?.map((tab) => {
              if(!tab?.visible) return null;
              if(tab.value === 'customers') {
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.value}
                    disabled={!tab.enabled}
                    className={`px-6 py-3.5 h-11 border-transparent border-t  w-[215px] text-sm ${tab.value === activeTab &&
                      " rounded-none border-blue-700 text-blue-700 shadow bg-[#EFF4FF]"
                      }`}
                  >
                    <div className="flex gap-2 items-center">
                      {tab.label} <span className=" text-center">
                        {
                          customerCount?.size
                        }
                      </span>
                    </div>
                  </TabsTrigger>
                )
              }
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.value}
                  disabled={!tab.enabled}
                  className={`px-6 py-3.5 border-transparent border-t h-11 w-[215px] text-sm ${tab.value === activeTab &&
                    " rounded-none  border-blue-700 text-blue-700 shadow bg-[#EFF4FF]"
                    }`}
                >
                  <div className="flex gap-2 items-center">
                    {tab.label}
                  </div>
                </TabsTrigger>
              );
            })}
          </TabsList>
          <div className="flex w-full justify-between gap-10">            
            <div className="flex-1">
              <TabsContent
                data-state={activeTab === "overview" ? "active" : ""}
                className=" gap-10 flex flex-col rounded-xl w-full overflow-auto"
                value="overview"
              >
                <div className="p-3">
                  <Overview
                    param={param}
                    orderdetails={orderDetails?.overview!!}
                    setIsEditing={setIsEditing}
                    setTempNote={setTempNote}
                    setNote={setNote}
                    isEditing={isEditing}
                    openDetailNote={openDetailNote}
                    note={note}
                    tempNote={tempNote}
                    buttonState={isDisabledAll.disabled}
                    onCustomerClick={(customerId: number) => {
                      setTab("customers");
                      setSelectedCustomerId(customerId);
                    }}
                    setTab={val=>{
                      setTab(val);
                    }}
                  />
                </div>
              </TabsContent>
              <TabsContent
                className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                value="amount-charges">
                <div className="p-3">
                  <AmountCharges
                    orderdetails={orderDetails?.amount_and_charges!!}
                  />
                </div>
              </TabsContent>
              <TabsContent
                className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                value="customers">
                <div className="p-3">
                  <Customers
                    order_documents={orderDetails?.order_documents || []}
                    customers={orderDetails?.customers!!}
                    selectedCustomerId={selectedCustomerId}
                    onCustomerTabClick={(customerId: number) => setSelectedCustomerId(customerId)}
                    documentsButtonState={documentsButtonState}
                    paymentButtonState={paymentButtonState}
                  />
                </div>
              </TabsContent>
              <TabsContent
                className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                value="delivery">
                <div className="px-3">
                  <Delivery editButtonState={ deliveryButtonState?.disabled! } products={orderDetails?.customers?.product_customer!!} customers={orderDetails?.customers!!} deliveryButtonState={deliveryButtonState} deliveriesData={orderDetails?.delivery!!} milestone={milestone}/>
                </div>
              </TabsContent>
              <TabsContent
                className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                value="payments">
                <div className="px-3">
                  <Payments
                    payable={orderDetails?.overview?.amount_and_charges?.total_payable as number}
                    paid={orderDetails?.overview?.amount_and_charges?.total_paid as number}
                    balance={orderDetails?.overview?.amount_and_charges?.balance as number}
                    payments={orderDetails?.payment_detail!!}
                    paymentButtonState={paymentButtonState}
                  />
                </div>
              </TabsContent>
              <TabsContent
                className="gap-10 flex flex-col rounded-xl w-full overflow-auto"
                value="other-details">
                <div className="px-3">
                  <OtherDetails orderdetails={orderDetails?.other_detail!!} />
                </div>
              </TabsContent>
            </div>
            {
              activeTab === 'customers' &&
              <div className="flex flex-col w-[300px] h-full mt-5">
                <MilestoneTracker milestones={milestone}/>
              </div>
            }
          </div>
        </Tabs>
      </div>
      <Toaster position="top-right" />
      {isConfirmBooking && (
        <ConfirmBookingModal
          onClose={() => setIsConfirmBooking(false)}
          onSubmit={handleConfirmSubmit}
          initialData={oldSchemaData||[]}
        />
      )}
    </>
  );
};

export default GenericOrderDetailsPage;